import { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, Trophy, Snowflake } from 'lucide-react';
import { useUserStreak } from '@/hooks/useUserStreak';
import { useStreakFreeze } from '@/hooks/useStreakFreeze';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export const GamificationNavBadge = memo(() => {
  const { user } = useAuth();
  const { streak, loading } = useUserStreak();
  const { getFreezeData } = useStreakFreeze();
  const [freezeTokens, setFreezeTokens] = useState(3);

  useEffect(() => {
    const loadFreezeData = async () => {
      const data = await getFreezeData();
      if (data) {
        setFreezeTokens(data.freezeTokens);
      }
    };
    if (user) loadFreezeData();
  }, [user, getFreezeData]);

  if (!user || loading) return null;

  const currentStreak = streak?.currentStreak || 0;
  const level = streak?.level || 1;
  const isOnFire = currentStreak >= 3;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link to="/achievements" className="relative group">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-full",
              "bg-gradient-to-r transition-all duration-300",
              isOnFire 
                ? "from-orange-500/20 to-amber-500/20 border border-orange-500/30"
                : "from-primary/10 to-primary/5 border border-primary/20",
              "hover:scale-105 hover:shadow-lg cursor-pointer"
            )}
          >
            {/* Streak flame/trophy */}
            <motion.div
              animate={isOnFire ? {
                scale: [1, 1.2, 1],
                rotate: [0, -5, 5, 0],
              } : {}}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
            >
              {isOnFire ? (
                <Flame className="h-4 w-4 text-orange-500" />
              ) : (
                <Trophy className="h-4 w-4 text-primary" />
              )}
            </motion.div>

            {/* Streak count */}
            <span className={cn(
              "text-xs font-bold",
              isOnFire ? "text-orange-500" : "text-primary"
            )}>
              {currentStreak}
            </span>

            {/* Level badge */}
            <div className="flex items-center gap-0.5 bg-background/50 rounded-full px-1.5 py-0.5">
              <span className="text-[10px] font-semibold text-muted-foreground">Lv</span>
              <span className="text-xs font-bold text-foreground">{level}</span>
            </div>

            {/* Freeze indicator */}
            {freezeTokens > 0 && (
              <div className="flex items-center">
                <Snowflake className="h-3 w-3 text-cyan-400" />
                <span className="text-[10px] font-medium text-cyan-400">{freezeTokens}</span>
              </div>
            )}

            {/* Animated glow for streaks */}
            {isOnFire && (
              <motion.div
                className="absolute inset-0 rounded-full bg-orange-500/20"
                animate={{
                  opacity: [0.5, 0.2, 0.5],
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            )}
          </motion.div>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="p-3">
        <div className="space-y-1">
          <p className="font-semibold">🔥 {currentStreak} Day Streak</p>
          <p className="text-xs text-muted-foreground">Level {level} • {freezeTokens} freeze{freezeTokens !== 1 ? 's' : ''} left</p>
          <p className="text-xs text-primary">Click to view achievements</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
});

GamificationNavBadge.displayName = 'GamificationNavBadge';
